import { FC, useMemo, type JSX } from 'react';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Select } from '../components/Select';
import { ImageType, OfferedProductType, OptionType } from '../types/SellTradeForm.types';
import { onlyNumbers, preventMinuses } from '../utils/inputUtils';
import { CloseIcon } from '../../../icons';
import { useAppSelector } from '../../../hooks/reduxHooks';
import { customStyles } from './OffererForm';
import { Box, Typography } from '@mui/material';
import { UploadFile } from '../../ServicePage/ContactService/UploadFile';
import { Files, FileWrapper } from '../../ServicePage/ContactService/styles';
import addAdditionalWatchIcon from '../../../assets/images/svg/addAdditionalWatchIcon.svg';
import Image from 'next/image';

export type Props = {
  offeredProducts: OfferedProductType[];
  manageOfferedProducts: (
    propName: keyof OfferedProductType,
    payload: string | number | boolean | JSX.Element | ImageType[],
    index: number
  ) => void;
  addProduct: (productType: string) => void;
  brands: OptionType[];
  models: OptionType[][];
  currencyOptions: OptionType[];
  handleBlur: (key: string, value: string, which: string, index: number, request?: boolean) => void;
  removeImage: (image: ImageType, index: number) => void;
};

export const WatchesSellForm: FC<Props> = ({
  offeredProducts,
  manageOfferedProducts,
  addProduct,
  brands,
  models,
  currencyOptions,
  handleBlur,
  removeImage,
}) => {
  const locale = useAppSelector((st) => st.locale);

  const messages = useMemo(() => {
    return {
      sell_form_step_2: locale['sell_form_step_2'],
      sell_form_step_number: locale['sell_form_step_number'],
      sell_form_brand_title: locale['sell_form_brand_title'],
      sell_form_brand_placeholder: locale['sell_form_brand_placeholder'],
      sell_form_model_title: locale['sell_form_model_title'],
      sell_form_model_placeholder: locale['sell_form_model_placeholder'],
      sell_form_reference_title: locale['sell_form_reference_title'],
      sell_form_reference_placeholder: locale['sell_form_reference_placeholder'],
      sell_form_condition_title: locale['sell_form_condition_title'],
      sell_form_condition_placeholder: locale['sell_form_condition_placeholder'],
      sell_form_box_papers_title: locale['sell_form_box_papers_title'],
      sell_form_box_checkbox: locale['sell_form_box_checkbox'],
      sell_form_papers_checkbox: locale['sell_form_papers_checkbox'],
      sell_form_add_photos_title: locale['sell_form_add_photos_title'],
      sell_form_add_photos_info: locale['sell_form_add_photos_info'],
      sell_form_price_title: locale['sell_form_price_title'],
      sell_form_price_placeholder: locale['sell_form_price_placeholder'],
      sell_form_currency_title: locale['sell_form_currency_title'],
      sell_form_currency_placeholder: locale['sell_form_currency_placeholder'],
      sell_form_comments_title: locale['sell_form_comments_title'],
      sell_form_comments_placeholder: locale['sell_form_comments_placeholder'],
      sell_form_have_more_title: locale['sell_form_have_more_title'],
      sell_form_add_more_watch_title: locale['sell_form_add_more_watch_title'],
      conditions: [
        { value: 0, label: locale['sell_trade_types_condition_new'] },
        { value: 1, label: locale['sell_trade_types_condition_mint'] },
        { value: 2, label: locale['sell_trade_types_condition_old'] },
      ],
      attachment: locale['general_attachment_star'],
    };
  }, [locale]);

  return (
    <div className='form-item-container'>
      {offeredProducts.map((product, index) => (
        <div className='form-item-container' key={index}>
          {offeredProducts?.length > 1 ? (
            <>
              <span>{`${messages.sell_form_step_number} ${index + 1}`}</span>
              <hr className='custom-hr gray-hr' />
            </>
          ) : null}
          <div className='form-row form-flex-row'>
            <div className='input-wrapper'>
              <Select
                options={brands}
                value={brands?.find((item) => item.label === product.brandName) || null}
                onChange={(option) => {
                  manageOfferedProducts('brandName', option?.label, index);
                  handleBlur('brandName', option?.label, 'offered', index, true);
                }}
                onBlur={(e) => {
                  e.target.value &&
                    !brands?.filter(
                      (item) =>
                        typeof item?.label === 'string' &&
                        item?.label?.toLowerCase()?.includes(e.target.value?.toLowerCase())
                    )?.length &&
                    manageOfferedProducts('brandName', e.target.value, index);
                  handleBlur('brandName', e.target.value || product.brandName, 'offered', index);
                }}
                className='custom-select'
                placeholder={`${messages.sell_form_brand_placeholder}`}
                dropdownStyles={customStyles}
                isClearable
                loadingText='Brands are loading...'
              />
            </div>
            <div className='input-wrapper  input-wrapper-right'>
              <Select
                options={models[index]}
                value={models?.[index]?.find((item) => item.label === product.productName) || null}
                onChange={(option) => {
                  manageOfferedProducts('productName', option?.label, index);
                }}
                onBlur={(e) => {
                  e.target.value &&
                    !models?.[index]?.filter(
                      (item) =>
                        typeof item?.label === 'string' &&
                        item?.label?.toLowerCase()?.includes(e.target.value?.toLowerCase())
                    )?.length &&
                    manageOfferedProducts('productName', e.target.value, index);
                  handleBlur('productName', e.target.value, 'offered', index);
                }}
                className='custom-select'
                placeholder={`${messages.sell_form_model_placeholder}`}
                dropdownStyles={customStyles}
                isClearable
                loadingText='Models are loading...'
              />
            </div>
          </div>
          <div className='form-row form-flex-row'>
            <div className='input-wrapper'>
              <input
                type='text'
                value={product?.refNumber}
                onChange={(e) => manageOfferedProducts('refNumber', e.target.value, index)}
                className='custom-input'
                placeholder={`${messages.sell_form_reference_placeholder}`}
              />
            </div>
            <div className='input-wrapper input-wrapper-right'>
              <Select
                options={messages.conditions}
                value={messages.conditions?.find((item) => item.value === product?.condition) || null}
                onChange={(option) => {
                  manageOfferedProducts('condition', option?.value, index);
                }}
                className='custom-select'
                placeholder={`${messages.sell_form_condition_placeholder}`}
                dropdownStyles={customStyles}
                isClearable
              />
            </div>
          </div>
          <hr className='custom-hr gray-hr hr-sell-form-2-bottom' />
          <div className='checkbox-container'>
            <Typography
              variant='h4'
              style={{
                fontSize: '24px',
                lineHeight: '29px',
                letterSpacing: '1px',
                color: '#535353',
                marginBottom: '8px',
              }}
            >
              {messages.sell_form_box_papers_title}
            </Typography>

            <FormControlLabel
              style={{ maxHeight: '21px' }}
              control={
                <Checkbox
                  className='checkbox'
                  checked={product?.hasBox}
                  onChange={(e) => manageOfferedProducts('hasBox', e.target.checked, index)}
                />
              }
              label={<label className='input-label big-label '>{`${messages.sell_form_box_checkbox}`}</label>}
            />
            <FormControlLabel
              style={{ maxHeight: '21px' }}
              control={
                <Checkbox
                  className='checkbox'
                  checked={product?.hasPaper}
                  onChange={(e) => manageOfferedProducts('hasPaper', e.target.checked, index)}
                />
              }
              label={<label className='input-label big-label'>{`${messages.sell_form_papers_checkbox}`}</label>}
            />
          </div>
          <hr className='custom-hr gray-hr-bottom' />
          <Box mb={1} className='upload-files'>
            <Typography
              variant='body1'
              fontSize='16px'
              letterSpacing={2}
              marginBottom='12px'
              lineHeight='20px !important'
              fontWeight='400 !important'
            >
              {messages.attachment}
            </Typography>
            <UploadFile
              minHeight='88px'
              getFiles={(files: ImageType[]) => {
                manageOfferedProducts('images', files || [], index);
              }}
              title={locale.general_upload_file}
            />
            <div style={{ position: 'relative' }}>
              <Files>
                {product?.images?.map((image) => (
                  <FileWrapper style={{ position: 'relative' }}>
                    <span style={{}}>{`${image?.name.slice(0, 20)}${image?.name?.length > 20 ? '...' : ''}`}</span>
                    <div onClick={() => removeImage(image, index)}>
                      <CloseIcon size={12} />
                    </div>
                  </FileWrapper>
                ))}
              </Files>
            </div>
          </Box>
          <div className='group-container price-container'>
            <input
              type='number'
              value={product?.price}
              onChange={(e) => manageOfferedProducts('price', e.target.value, index)}
              onKeyDown={(e) => {
                onlyNumbers(e);
                preventMinuses(e);
              }}
              className='custom-input currency-input'
              placeholder={`${messages.sell_form_price_placeholder}`}
              min={0}
            />
            <Select
              options={currencyOptions}
              value={
                currencyOptions.find((item) => item.id === product.currency) ||
                currencyOptions.find((item) => item.id === 'EUR')
              }
              onChange={(option) => manageOfferedProducts('currency', option?.label, index)}
              className='custom-select currency-select'
              placeholder={`${messages.sell_form_currency_placeholder}`}
              dropdownStyles={customStyles}
              isClearable={false}
            />
          </div>
          <textarea
            className='custom-input text-area'
            value={product?.comment}
            onChange={(e) => manageOfferedProducts('comment', e.target.value, index)}
            placeholder={`${messages.sell_form_comments_placeholder}`}
          />
        </div>
      ))}
      <div className='input-label clickable-label clickable-label-sell' onClick={() => addProduct('offered')}>
        <Image src={addAdditionalWatchIcon.src} alt='Add' width={25} height={25} />
        <span className='add-watch-span'> {`${messages.sell_form_add_more_watch_title.toUpperCase()}`}</span>
      </div>
    </div>
  );
};
